import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
 Target,
 Users,
 Award,
 Globe,
 TrendingUp,
 Heart,
 CheckCircle,
 ArrowRight,
 Briefcase,
 Building2,
 Loader2,
} from "lucide-react";
import client from "../api/client";

export default function AboutUs() {
 const [statsData, setStatsData] = useState({
 jobsPosted: null,
 companies: null,
 professionals: null,
 successRate: null,
 });
 const [loadingStats, setLoadingStats] = useState(true);

 const formatNumber = (n) => {
 if (n === null || n === undefined) return "—";
 if (n >= 1000) return Intl.NumberFormat("en-US").format(n) + "+";
 return String(n);
 };

 useEffect(() => {
 let mounted = true;
 const fetchStats = async () => {
 try {
 setLoadingStats(true);
 const { data } = await client.get("/jobs/public/stats");
 if (!mounted || !data) return;
 setStatsData({
 jobsPosted: data.jobsPosted ?? 0,
 companies: data.companies ?? 0,
 professionals: data.professionals ?? 0,
 successRate: data.successRate ?? 0,
 });
 } catch (err) {
 console.error("Error fetching about stats:", err);
 } finally {
 if (mounted) setLoadingStats(false);
 }
 };
 fetchStats();
 return () => {
 mounted = false;
 };
 }, []);

 const stats = [
 {
 number: loadingStats ? "..." : formatNumber(statsData.jobsPosted),
 label: "Jobs Posted",
 icon: Briefcase,
 color: "from-blue-500 to-cyan-500",
 },
 {
 number: loadingStats ? "..." : formatNumber(statsData.companies),
 label: "Companies",
 icon: Building2,
 color: "from-purple-500 to-pink-500",
 },
 {
 number: loadingStats ? "..." : formatNumber(statsData.professionals),
 label: "Professionals",
 icon: Users,
 color: "from-green-500 to-emerald-500",
 },
 {
 number: loadingStats
 ? "..."
 : statsData.successRate !== null
 ? `${statsData.successRate}%`
 : "—",
 label: "Success Rate",
 icon: TrendingUp,
 color: "from-orange-500 to-red-500",
 },
 ];

 const values = [
 {
 icon: Heart,
 title: "Passion",
 description: "We're passionate about connecting talent with opportunity.",
 color: "text-red-500",
 bg: "bg-red-50 ",
 },
 {
 icon: Target,
 title: "Excellence",
 description: "Striving for excellence in every connection we make.",
 color: "text-blue-500",
 bg: "bg-blue-500/10 ",
 },
 {
 icon: Users,
 title: "Community",
 description: "Building a strong professional community.",
 color: "text-green-500",
 bg: "bg-green-500/10 ",
 },
 {
 icon: Globe,
 title: "Innovation",
 description: "Constantly innovating to improve your job search.",
 color: "text-purple-500",
 bg: "bg-purple-500/10 ",
 },
 ];

 const features = [
 "AI-powered job matching",
 "Personalized career guidance",
 "Real-time application tracking",
 "Interview preparation resources",
 "Company insights and reviews",
 "Remote work opportunities",
 "Salary negotiation support",
 "Career development webinars",
 ];

 return (
 <div className="min-h-screen ">
 {/* Hero Section */}
 <section className="relative bg-gradient-to-br from-primary/5 via-background to-primary/10 py-12 sm:py-16 md:py-20 lg:py-24">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center max-w-4xl mx-auto">
 <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
 About <span className="text-primary">Shebeka</span>
 </h1>
 <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-4 sm:mb-6">
 Building bridges between talent and opportunity
 </p>
 <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-3xl mx-auto mb-6 sm:mb-8">
 Shebeka (meaning "bridge" in Amharic) is Ethiopia's premier job
 portal, connecting ambitious professionals with leading companies
 across Africa and beyond.
 </p>
 <Link to="/jobs">
 <Button size="lg" className="group h-11 sm:h-12 px-6 sm:px-8">
 Explore Jobs
 <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
 </Button>
 </Link>
 </div>
 </div>
 </section>

 {/* Stats Section */}
 <section className="py-12 sm:py-16 md:py-20">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
 {stats.map((stat) => {
 const Icon = stat.icon;
 return (
 <Card
 key={stat.label}
 className="group hover:shadow-lg transition-all duration-300 hover:scale-105 border-border"
 >
 <CardContent className="p-4 sm:p-6 text-center">
 <div className="flex justify-center mb-3 sm:mb-4">
 <div
 className={`p-2.5 sm:p-3 rounded-xl bg-gradient-to-r ${stat.color} shadow-lg`}
 >
 <Icon className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
 </div>
 </div>
 {loadingStats ? (
 <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
 ) : (
 <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-1">
 {stat.number}
 </p>
 )}
 <p className="text-xs sm:text-sm text-muted-foreground">
 {stat.label}
 </p>
 </CardContent>
 </Card>
 );
 })}
 </div>
 </div>
 </section>

 {/* Our Story Section */}
 <section className="py-12 sm:py-16 md:py-20 ">
 <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
 <Card className="border-border shadow-lg">
 <CardContent className="p-6 sm:p-8 md:p-10 lg:p-12">
 <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-6 sm:mb-8 text-center">
 Our Story
 </h2>
 <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">
 <div className="space-y-4 sm:space-y-6">
 <p className="text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed">
 Founded in 2023, Shebeka was born from a simple observation:
 talented professionals in Ethiopia and across Africa face
 significant challenges in connecting with meaningful
 employment opportunities.
 </p>
 <p className="text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed">
 Our founders, seasoned professionals in tech and HR,
 recognized the need for a platform that not only lists jobs
 but actively bridges the gap between employer needs and
 candidate aspirations.
 </p>
 <p className="text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed">
 Today, Shebeka has grown into a comprehensive ecosystem
 supporting career growth, skill development, and
 professional networking.
 </p>
 </div>
 <div className="flex justify-center">
 <div className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80">
 <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/20 to-primary/10 animate-pulse"></div>
 <div className="absolute inset-8 rounded-full bg-gradient-to-r from-primary/30 to-primary/20 animate-pulse delay-75"></div>
 <div className="absolute inset-16 rounded-full bg-gradient-to-r from-primary/40 to-primary/30 flex items-center justify-center">
 <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-primary">
 SHEBEKA
 </span>
 </div>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 </section>

 {/* Our Values */}
 <section className="py-12 sm:py-16 md:py-20">
 <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
 <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-8 sm:mb-12 text-center">
 Our Values
 </h2>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
 {values.map((value) => {
 const Icon = value.icon;
 return (
 <Card
 key={value.title}
 className="group hover:shadow-lg transition-all duration-300 hover:scale-105 border-border"
 >
 <CardContent className="p-5 sm:p-6">
 <div
 className={`p-2.5 sm:p-3 rounded-xl ${value.bg} w-fit mb-3 sm:mb-4`}
 >
 <Icon
 className={`w-5 h-5 sm:w-6 sm:h-6 ${value.color}`}
 />
 </div>
 <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2 sm:mb-3">
 {value.title}
 </h3>
 <p className="text-sm sm:text-base text-muted-foreground">
 {value.description}
 </p>
 </CardContent>
 </Card>
 );
 })}
 </div>
 </div>
 </section>

 {/* Features */}
 <section className="py-12 sm:py-16 md:py-20 ">
 <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
 <Card className="border-border shadow-lg">
 <CardContent className="p-6 sm:p-8 md:p-10 lg:p-12">
 <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-6 sm:mb-8 text-center">
 What Makes Shebeka Different
 </h2>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
 {features.map((feature) => (
 <div
 key={feature}
 className="flex items-center gap-3 p-3 sm:p-4 rounded-lg hover: transition-colors"
 >
 <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0" />
 <span className="text-sm sm:text-base text-foreground">
 {feature}
 </span>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 </div>
 </section>

 {/* CTA Section - Show for everyone */}
 <section className="py-12 sm:py-16 md:py-20">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
 <Card className="border-2 border-primary/20 shadow-xl">
 <CardContent className="p-6 sm:p-8 md:p-10 lg:p-12">
 <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4 sm:mb-6">
 Join the Shebeka Community
 </h2>
 <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-10 max-w-2xl mx-auto">
 Whether you're looking for your next career move or seeking top
 talent, Shebeka is your bridge to success.
 </p>
 <div className="flex flex-col sm:flex-row gap-4 justify-center">
 <Link to="/register">
 <Button
 size="lg"
 className="w-full sm:w-auto h-11 sm:h-12 px-6 sm:px-8"
 >
 Start Job Hunting
 </Button>
 </Link>
 <Link to="/contact">
 <Button
 size="lg"
 variant="outline"
 className="w-full sm:w-auto h-11 sm:h-12 px-6 sm:px-8 border-2"
 >
 Get In Touch
 </Button>
 </Link>
 </div>
 </CardContent>
 </Card>
 </div>
 </section>
 </div>
 );
}
